import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import StatusBadge from '@/components/ui/StatusBadge';
import { Panel } from '@/components/ui/Panel';
import { FEEDBACK_KINDS } from './FeedbackWidget';

/** Журнал замечаний (перенос из zakup): автор видит свои и принимает исправления; admin ведёт все, ставит статусы,
 * запускает робота и создаёт задачу в GitHub. Статусы как в zakup: new → work → testing → done; reject. */
type Ev = { id: number; type: string; from_value: string | null; to_value: string | null; text: string | null; created_at: string; author: string | null };
export type FeedbackItem = {
  id: number;
  kind: string;
  status: string;
  priority: string;
  side: 'treejar' | 'partner';
  partner_id: string | null;
  page: string | null;
  screen: string | null;
  role: string | null;
  message: string;
  note: string | null;
  created_at: string;
  updated_at: string;
  user_id: string;
  user_name: string | null;
  has_screenshot: boolean;
  fix_state: string | null;
  fix_note: string | null;
  github_issue_url: string | null;
  comments: number;
  events?: Ev[];
};

const STATUS_TONE: Record<string, 'primary' | 'secondary' | 'accent' | 'danger' | 'neutral'> = { new: 'neutral', work: 'secondary', testing: 'accent', done: 'primary', reject: 'danger' };
const STATUSES = ['new', 'work', 'testing', 'done', 'reject'];
const PRIORITIES = ['low', 'normal', 'high', 'critical'];
const FILTERS: [string, string][] = [
  ['new,work,testing', 'open'],
  ['testing', 'awaiting'],
  ['done,reject', 'closed'],
  ['new,work,testing,done,reject', 'all'],
];

function useList<T>(path: string | null, deps: unknown[]) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(!!path);
  const [error, setError] = useState<string | null>(null);
  const reload = useCallback(async () => {
    if (!path) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setData(await api.get<T>(path));
    } catch (e) {
      setError((e as ApiError).code);
    } finally {
      setLoading(false);
    }
  }, [path]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => void reload(), [reload, ...deps]);
  return { data, loading, error, reload };
}

export default function FeedbackBoard({ embedded = false }: { embedded?: boolean }) {
  const { t, i18n } = useTranslation();
  const { me } = useAuth();
  const admin = me?.side === 'staff' && me.role === 'admin';
  const [status, setStatus] = useState(FILTERS[0][0]);
  const [mine, setMine] = useState(!admin);
  const [side, setSide] = useState('');
  const q = `/feedback?status=${status}${mine ? '&mine=1' : ''}${side ? `&side=${side}` : ''}`;
  const list = useList<FeedbackItem[]>(q, [status, mine, side]);
  const [openId, setOpenId] = useState<number | null>(() => Number(location.hash.slice(1)) || null);
  const item = useList<FeedbackItem>(openId ? `/feedback/${openId}` : null, [openId]);
  const [text, setText] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const locale = i18n.language === 'ru' ? 'ru-RU' : 'en-GB';

  useEffect(() => {
    const h = () => void list.reload();
    window.addEventListener('feedback:changed', h);
    return () => window.removeEventListener('feedback:changed', h);
  }, [list]);

  const act = async (fn: () => Promise<unknown>) => {
    setErr(null);
    setBusy(true);
    try {
      await fn();
      await item.reload();
      await list.reload();
    } catch (e) {
      setErr((e as ApiError).code);
    } finally {
      setBusy(false);
    }
  };
  const it = item.data;
  const btn = 'h-8 cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-2.5 text-[11.5px] font-medium text-foreground-700 hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50';
  const primaryBtn = 'h-8 cursor-pointer whitespace-nowrap rounded-md bg-primary-600 px-3 text-[11.5px] font-semibold text-background-50 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50';
  const select = 'h-8 cursor-pointer rounded-md border border-background-300 bg-background-50 px-2 text-[11.5px] text-foreground-700 outline-none focus:border-primary-400';
  const eventText = (e: Ev) => {
    if (e.type === 'status') return t('fb.eventStatus', { from: t(`fb.status.${e.from_value}`, e.from_value ?? ''), to: t(`fb.status.${e.to_value}`, e.to_value ?? '') });
    if (e.type === 'priority') return t('fb.eventPriority', { from: t(`fb.priority.${e.from_value}`, e.from_value ?? ''), to: t(`fb.priority.${e.to_value}`, e.to_value ?? '') });
    if (e.type === 'fix') return t('fb.eventFix', { state: e.to_value });
    if (e.type === 'issue') return t('fb.eventIssue');
    return t('fb.eventComment');
  };

  return (
    <div className={embedded ? 'mt-5' : ''}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1 rounded-full border border-background-200 bg-background-100 p-1">
          {FILTERS.map(([v, k]) => (
            <button key={v} type="button" onClick={() => setStatus(v)} className={`cursor-pointer whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-medium ${status === v ? 'bg-background-50 text-foreground-950' : 'text-foreground-500 hover:text-foreground-800'}`}>
              {t(`fb.filter.${k}`)}
            </button>
          ))}
        </div>
        {admin && (
          <>
            <label className="flex cursor-pointer items-center gap-1.5 text-[11.5px] text-foreground-600">
              <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} className="h-3.5 w-3.5 accent-primary-600" /> {t('fb.onlyMine')}
            </label>
            <select value={side} onChange={(e) => setSide(e.target.value)} className={select} aria-label={t('fb.col.side')}>
              <option value="">{t('fb.sideAll')}</option>
              <option value="treejar">{t('fb.sideTreejar')}</option>
              <option value="partner">{t('fb.sidePartner')}</option>
            </select>
          </>
        )}
      </div>
      <p className="mt-2 text-[11.5px] leading-4 text-foreground-500">{t('fb.boardHint')}</p>
      {(err || list.error) && (
        <p className="mt-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[11.5px] text-rose-700">
          {t('ui.error')}: {err || list.error}{' '}
          <button type="button" onClick={() => void list.reload()} className="cursor-pointer font-semibold underline">
            {t('ui.retry')}
          </button>
        </p>
      )}

      <div className="mt-3 grid grid-cols-1 gap-4 xl:grid-cols-5">
        <Panel padded={false} className="xl:col-span-3">
          <div className="overflow-x-auto thin-scroll">
            <table className="w-full min-w-[640px] border-collapse text-[12px]">
              <thead>
                <tr className="border-b border-background-200 bg-background-100/70 text-left text-[10px] uppercase tracking-[0.07em] text-foreground-500">
                  <th className="px-3 py-2 font-medium">№</th>
                  <th className="px-3 py-2 font-medium">{t('fb.col.status')}</th>
                  <th className="px-3 py-2 font-medium">{t('fb.col.type')}</th>
                  <th className="px-3 py-2 font-medium">{t('fb.col.summary')}</th>
                  <th className="px-3 py-2 font-medium">{t('fb.col.screen')}</th>
                  <th className="px-3 py-2 font-medium">{t('fb.col.author')}</th>
                  <th className="px-3 py-2 font-medium">{t('fb.col.when')}</th>
                </tr>
              </thead>
              <tbody>
                {list.loading && !list.data && (
                  <tr>
                    <td colSpan={7} className="px-3 py-6 text-center text-foreground-400">
                      {t('ui.loading')}
                    </td>
                  </tr>
                )}
                {list.data && list.data.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-3 py-6 text-center text-foreground-400">
                      {t('fb.empty')}
                    </td>
                  </tr>
                )}
                {list.data?.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => {
                      setOpenId(r.id);
                      location.hash = String(r.id);
                    }}
                    className={`cursor-pointer border-b border-background-200/70 last:border-0 hover:bg-background-100/60 ${openId === r.id ? 'bg-primary-50/50' : ''}`}
                  >
                    <td className="tnum px-3 py-2.5 text-foreground-500">#{r.id}</td>
                    <td className="px-3 py-2.5">
                      <StatusBadge tone={STATUS_TONE[r.status] ?? 'neutral'}>{t(`fb.status.${r.status}`)}</StatusBadge>
                      {r.fix_state && <p className="mt-1 text-[10px] text-accent-700">{t('fb.robot', { state: r.fix_state })}</p>}
                    </td>
                    <td className="px-3 py-2.5 text-foreground-700">{t(`fb.kind.${r.kind}`)}</td>
                    <td className="max-w-[320px] px-3 py-2.5 text-foreground-900">
                      <span className="line-clamp-2">{r.message}</span>
                      {r.comments > 0 && <span className="text-[10.5px] text-foreground-400"> · {t('fb.comments', { n: r.comments })}</span>}
                    </td>
                    <td className="px-3 py-2.5 text-foreground-500">{r.screen}</td>
                    <td className="px-3 py-2.5 text-foreground-500">
                      {r.user_name}
                      {admin && <span className="block text-[10px] text-foreground-400">{r.side === 'partner' ? t('fb.sidePartner') : t('fb.sideTreejar')}</span>}
                    </td>
                    <td className="tnum px-3 py-2.5 text-foreground-500">{new Date(r.created_at).toLocaleDateString(locale)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel className="xl:col-span-2">
          {!it ? (
            <p className="text-[12px] text-foreground-400">{item.loading ? t('ui.loading') : t('fb.selectHint')}</p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-[14px] font-semibold text-foreground-950">
                  #{it.id} · {t(`fb.kind.${it.kind}`)}
                </h2>
                <StatusBadge tone={STATUS_TONE[it.status] ?? 'neutral'}>{t(`fb.status.${it.status}`)}</StatusBadge>
                <StatusBadge dot={false}>{t(`fb.priority.${it.priority}`)}</StatusBadge>
              </div>
              <p className="mt-1 text-[10.5px] text-foreground-500">
                {it.user_name} · {new Date(it.created_at).toLocaleString(locale)} · {it.screen} · {it.page}
              </p>
              <p className="mt-3 whitespace-pre-wrap text-[12.5px] leading-5 text-foreground-900">{it.message}</p>
              {it.has_screenshot && (
                <a href={`/api/feedback/${it.id}/screenshot`} target="_blank" rel="noreferrer" className="mt-3 block">
                  <img src={`/api/feedback/${it.id}/screenshot`} alt={t('fb.screenshot')} className="max-h-[260px] w-auto rounded-md border border-background-200" />
                </a>
              )}
              {it.note && (
                <p className="mt-3 rounded-md border border-accent-200 bg-accent-50 px-3 py-2 text-[11.5px] text-accent-800">
                  {t('fb.note')}: {it.note}
                </p>
              )}
              {it.fix_note && (
                <p className="mt-2 rounded-md border border-background-200 bg-background-100 px-3 py-2 text-[11.5px] text-foreground-700">
                  {t('fb.robotNote')}: {it.fix_note}
                </p>
              )}
              {it.github_issue_url && (
                <a href={it.github_issue_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-[11.5px] font-semibold text-primary-700 hover:text-primary-800">
                  <i className="ri-github-line" /> {t('fb.issueCreated')}
                </a>
              )}
              <h3 className="mt-4 text-[11px] font-semibold uppercase tracking-[0.07em] text-foreground-500">{t('fb.history')}</h3>
              <ul className="mt-1.5 space-y-1 text-[11.5px] leading-4">
                {it.events?.map((e) => (
                  <li key={e.id} className="text-foreground-700">
                    <span className="text-foreground-400">
                      {new Date(e.created_at).toLocaleString(locale)} · {e.author ?? t('fb.system')}:
                    </span>{' '}
                    {eventText(e)}
                    {e.text ? ` — ${e.text}` : ''}
                  </li>
                ))}
              </ul>
              <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder={t('fb.commentPlaceholder')} rows={3} className="mt-3 w-full rounded-md border border-background-300 bg-background-50 px-2.5 py-2 text-[12px] outline-none focus:border-primary-400" />
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <button type="button" disabled={!text.trim() || busy} className={btn} onClick={() => act(async () => { await api.post(`/feedback/${it.id}/comments`, { text }); setText(''); })}>
                  {t('fb.sendComment')}
                </button>
                {it.status === 'testing' && it.user_id === me?.id && (
                  <>
                    <button type="button" disabled={busy} className={primaryBtn} onClick={() => act(() => api.patch(`/feedback/${it.id}`, { status: 'done' }))}>
                      {t('fb.accept')}
                    </button>
                    <button type="button" disabled={!text.trim() || busy} className={btn} onClick={() => act(async () => { await api.patch(`/feedback/${it.id}`, { status: 'work', comment: text }); setText(''); })}>
                      {t('fb.returnToWork')}
                    </button>
                  </>
                )}
                {admin && (
                  <>
                    <select value={it.status} disabled={busy} onChange={(e) => act(() => api.patch(`/feedback/${it.id}`, { status: e.target.value, comment: text || undefined }))} className={select} aria-label={t('fb.col.status')}>
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {t(`fb.status.${s}`)}
                        </option>
                      ))}
                    </select>
                    <select value={it.priority} disabled={busy} onChange={(e) => act(() => api.patch(`/feedback/${it.id}`, { priority: e.target.value }))} className={select} aria-label={t('fb.col.priority')}>
                      {PRIORITIES.map((p) => (
                        <option key={p} value={p}>
                          {t(`fb.priority.${p}`)}
                        </option>
                      ))}
                    </select>
                    <button type="button" disabled={busy || it.fix_state === 'queued' || it.fix_state === 'running'} className={btn} onClick={() => act(() => api.post(`/feedback/${it.id}/fix`))} title={t('fb.runFixHint')}>
                      {it.fix_state === 'queued' ? t('fb.queued') : it.fix_state === 'running' ? t('fb.running') : t('fb.runFix')}
                    </button>
                    {!it.github_issue_url && (
                      <button type="button" disabled={busy} className={btn} onClick={() => act(() => api.post(`/feedback/${it.id}/issue`))}>
                        <i className="ri-github-line mr-1" />
                        {t('fb.createIssue')}
                      </button>
                    )}
                  </>
                )}
              </div>
              <p className="mt-3 text-[10.5px] text-foreground-400">{t('fb.kindsHint', { kinds: FEEDBACK_KINDS.map((k) => t(`fb.kind.${k}`)).join(', ') })}</p>
            </>
          )}
        </Panel>
      </div>
    </div>
  );
}
